import JsonResponse from "csi/comm/JsonResponse";
import def from "csi/def";
import { View } from "csi/def/View";
import { ControllerSaveFunction } from "csi/middleware/common";
import reflection from "csi/reflection";
import { MassUpdateWizardStore } from "csi/store/MassUpdateWizardStore";
import { ICache, cnl } from "csi/typings";
import { CheckboxTreeNode, CheckboxTreeStore } from "designSystem/components/checkbox/CheckboxTree";
import { RadioOption } from "designSystem/components/radio/RadioGroup";
import { ILocaleProvider } from "designSystem/locale";
import { makeObservable } from "mobx";
import { massUpdateSharedDataSheets, massUpdateStyleBOMs } from "./MassUpdateBOMService";
import FormData from "csi/action/FormData";

export class PartMaterial extends CheckboxTreeNode {
	readonly sectionUrl: cnl;
	readonly sectionName: string;

	constructor(store: CheckboxTreeStore | undefined, uniqueId: string, name: string, sectionUrl: string, sectionName: string, state: boolean) {
		super(store, uniqueId, name, state);
		this.sectionUrl = sectionUrl;
		this.sectionName = sectionName;
	}
}

export class MassUpdateStyleBOMWizardStore extends MassUpdateWizardStore {
	constructor(
		onClose: () => void,
		onSave: ControllerSaveFunction,
		actionId: string,
		cache: ICache,
		formData: FormData,
		locale: ILocaleProvider,
		revisionURL: cnl,
		itemsMap: Map<string, CheckboxTreeNode>,
	) {
		super(onClose, onSave, cache, formData, locale, actionId, revisionURL, itemsMap);
		makeObservable(this);
		this.actionMessageRes = "MassUpdateStyleBOMs_Placements_Selected";
		this.validationMsgRes = "MassUpdateStyleBOMs_SelectionEmptyError";
		this.tableViewId = "ThemeBOMLinkedStyleBOMs";
		this.tableNodeType = "ApparelBOM";
		this.tableSubTitleRes = "MassUpdateStyleBOMs_SubTitle1";
		this.selectSectionTitleRes = "MassUpdateStyleBOMs_SelectThemeBOMPlacement";
	}

	/** Return table view definition to show on 2nd page of wizard */
	_defLinkedBOMsView = (): View => {
		const rc = reflection.getClass(this.tableNodeType );
		const defView = def.getClassViewDef(rc, this.tableViewId) as View;

		defView.setFormData(this.formData);
		return defView;
	};

	/** 
	 * @override 
	 * extension point to get Radio option array. 
	 * */
	_massUpdateOptions = (): RadioOption<boolean>[] => {
		return [
			{
				name: this.locale.resource("MassUpdateStyleBOMs_PushAllPlacements"),
				helperTextRes: "MassUpdateStyleBOMs_PushAllPlacements_HelperText",
				value: true,
			},
			{
				name: this.locale.resource("MassUpdateStyleBOMs_UpdatePlacementsOnly"),
				helperTextRes: "MassUpdateStyleBOMs_UpdatePlacementsOnly_HelperText",
				value: false,
			},
		];
	};

	/** 
	 * @override
	 *  extension point to call the Mass Update operation.
	 * */
	_massUpdateOperation = async (createRevision: boolean, urlRevision: cnl, pushAllPlacements: boolean, itemsSelected: cnl[], targetsSelected: cnl[]): Promise<JsonResponse> => {
		return massUpdateStyleBOMs(createRevision, urlRevision, pushAllPlacements, itemsSelected, targetsSelected);
	};
}



export class MassUpdateSharedBOMWizardStore extends MassUpdateWizardStore {
	constructor(
		onClose: () => void,
		onSave: ControllerSaveFunction,
		actionId: string,
		cache: ICache,
		formData: FormData,
		locale: ILocaleProvider,
		revisionURL: cnl,
		itemsMap: Map<string, CheckboxTreeNode>,
	) {
		super(onClose, onSave, cache, formData, locale, actionId, revisionURL, itemsMap);
		makeObservable(this);
		this.actionMessageRes = "MassUpdateSharedDataSheets_Placements_Selected";
		this.validationMsgRes = "MassUpdateSharedDataSheets_SelectionEmptyError";
		this.tableViewId = "LinkedSourceDataSheets";
		this.tableNodeType = "ApparelBOM";
		this.tableSubTitleRes = "MassUpdateSharedDataSheets_SubTitle1";
		this.selectSectionTitleRes = "MassUpdateSharedDataSheets_SelectThemeBOMPlacement";
		this.createRevision = true;
		this.disableCreateRevision = true;
	}

	/** Return table view definition to show on 2nd page of wizard */
	_defLinkedBOMsView = (): View => {
		const rc = reflection.getClass(this.tableNodeType );
		const defView = def.getClassViewDef(rc, this.tableViewId) as View;

		defView.setFormData(this.formData);
		return defView;
	};

	/** 
	 * @override 
	 * extension point to get Radio option array for Placements Settings. 
	 * */
	_massUpdateOptions = (): RadioOption<boolean>[] => {
		return [
			{
				name: this.locale.resource("MassUpdateSharedDataSheets_PushAllPlacements"),
				helperTextRes: "MassUpdateSharedDataSheets_PushAllPlacements_HelperText",
				value: true,
			},
			{
				name: this.locale.resource("MassUpdateSharedDataSheets_UpdatePlacementsOnly"),
				helperTextRes: "MassUpdateSharedDataSheets_UpdatePlacementsOnly_HelperText",
				value: false,
			},
		];
	};
	
	/** 
	 * @override 
	 * extension point to get Radio option array for Attributes Settings. 
	 * */
	_attributesSettingsOptions = (): RadioOption<boolean>[] => {
		return [
			{
				name: this.locale.resource("MassUpdateSharedDataSheets_DoNotOverrideAttributeValues"),
				helperTextRes: "MassUpdateSharedDataSheets_DoNotOverrideAttributeValuesHelperText",
				value: false,
			},
			{
				name: this.locale.resource("MassUpdateSharedDataSheets_OverrideAttributeValues"),
				helperTextRes: "MassUpdateSharedDataSheets_OverrideAttributeValuesHelperText",
				value: true,
			},
		];
	};
	

	/** 
	 * @override
	 *  extension point to call the Mass Update operation.
	 * */
	_massUpdateOperation = async (createRevision: boolean, urlRevision: cnl, pushAllPlacements: boolean, itemsSelected: cnl[], targetsSelected: cnl[]): Promise<JsonResponse> => {
		return massUpdateSharedDataSheets(createRevision, urlRevision, pushAllPlacements, itemsSelected, targetsSelected, this.overrideAttributes);
	};
}